import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FiUser, 
  FiHeart, 
  FiClock, 
  FiActivity, 
  FiCalendar, 
  FiClipboard,
  FiTrendingUp,
  FiAlertCircle,
  FiCheckCircle,
  FiMoon
} from 'react-icons/fi';

import { DashboardCard } from '../components/dashboard/DashboardCard';
import { StatCard } from '../components/dashboard/StatCard';
import { SleepChart } from '../components/dashboard/SleepChart';
import { Icon } from '../components/ui/Icon';
import { theme } from '../styles/theme';
import patientData from '../data/dummyPatientData.json';


const Wrapper = styled.div`
  min-height: 100vh;
  padding-top: 80px;
  background: ${theme.colors.gray[50]};
`;

const Container = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing.lg};
  }
`;

const DashboardContainer = styled.div`
  width: 100%;
`;



const PatientInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.lg};
  flex: 1;
`;

const PatientDetails = styled.div`
  text-align: center;
  
  h1 {
    color: ${theme.colors.primary};
    margin-bottom: ${theme.spacing.xs};
    font-size: ${theme.typography.sizes['2xl']};
  }
  
  p {
    color: ${theme.colors.gray[600]};
    margin: ${theme.spacing.xs} 0;
    font-size: ${theme.typography.sizes.sm};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing['2xl']};
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing['2xl']};
  
  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const ActivitiesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-bottom: ${theme.spacing.xl};
  gap: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const TopSection = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};
  
  @media (min-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const AllergiesCard = styled.div`
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%);
  border: 2px solid #EF4444;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  
  h3 {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.sm};
    margin: 0 0 ${theme.spacing.md} 0;
    color: #DC2626;
    font-size: ${theme.typography.sizes.lg};
    font-weight: ${theme.typography.weights.semibold};
  }
`;

const AllergyItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.sm};
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: ${theme.borderRadius.md};
  color: #991B1B;
  font-weight: ${theme.typography.weights.medium};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ActivityItem = styled.div<{ status: string }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  background: ${({ status }) => {
    switch (status) {
      case 'completed': return 'rgba(16, 185, 129, 0.1)';
      case 'pending': return 'rgba(245, 158, 11, 0.1)';
      default: return theme.colors.gray[50];
    }
  }};
  border-left: 4px solid ${({ status }) => {
    switch (status) {
      case 'completed': return '#10B981';
      case 'pending': return '#F59E0B';
      default: return theme.colors.gray[300];
    }
  }};
  margin-bottom: ${theme.spacing.sm};
`;

const ActivityTime = styled.span`
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  min-width: 60px;
`;

const ActivityDescription = styled.div`
  flex: 1;
  
  h4 {
    margin: 0 0 ${theme.spacing.xs} 0;
    color: ${theme.colors.gray[900]};
    font-size: ${theme.typography.sizes.sm};
  }
  
  p {
    margin: 0;
    color: ${theme.colors.gray[600]};
    font-size: ${theme.typography.sizes.xs};
  }
`;

const ActivityStatus = styled.div<{ status: string }>`
  color: ${({ status }) => {
    switch (status) {
      case 'completed': return '#10B981';
      case 'pending': return '#F59E0B';
      default: return theme.colors.gray[500];
    }
  }};
`;

const ListItem = styled.div`
  padding: ${theme.spacing.sm} 0;
  border-bottom: 1px solid ${theme.colors.gray[100]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  
  &:last-child {
    border-bottom: none;
  }
  
  strong {
    color: ${theme.colors.gray[900]};
  }
`;



const SleepCalendar = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};
`;

const SleepDay = styled.div<{ quality: string }>`
  padding: ${theme.spacing.sm};
  background: ${({ quality }) => {
    switch (quality) {
      case 'Excellent': return 'rgba(16, 185, 129, 0.1)';
      case 'Good': return 'rgba(34, 197, 94, 0.1)';
      case 'Fair': return 'rgba(245, 158, 11, 0.1)';
      case 'Poor': return 'rgba(239, 68, 68, 0.1)';
      default: return theme.colors.gray[50];
    }
  }};
  border: 2px solid ${({ quality }) => {
    switch (quality) {
      case 'Excellent': return '#10B981';
      case 'Good': return '#22C55E';
      case 'Fair': return '#F59E0B';
      case 'Poor': return '#EF4444';
      default: return theme.colors.gray[200];
    }
  }};
  border-radius: ${theme.borderRadius.md};
  text-align: center;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }
`;

const DayHeader = styled.div`
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.xs};
  text-transform: uppercase;
`;

const DateNumber = styled.div`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.gray[900]};
  margin-bottom: ${theme.spacing.xs};
`;

const SleepTime = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.gray[600]};
  margin: 2px 0;
  
  .label {
    font-weight: ${theme.typography.weights.medium};
    color: ${theme.colors.gray[700]};
  }
`;

const SleepDuration = styled.div`
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.bold};
  margin-top: ${theme.spacing.xs};
  color: ${theme.colors.primary};
`;

const QualityBadge = styled.div<{ quality: string }>`
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.medium};
  padding: 2px ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.sm};
  margin-top: ${theme.spacing.xs};
  background: ${({ quality }) => {
    switch (quality) {
      case 'Excellent': return '#10B981';
      case 'Good': return '#22C55E';
      case 'Fair': return '#F59E0B';
      case 'Poor': return '#EF4444';
      default: return theme.colors.gray[500];
    }
  }};
  color: ${theme.colors.white};
  text-transform: capitalize;
`;

const CareNote = styled.div`
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};
  background: ${theme.colors.neutral};
  border-radius: ${theme.borderRadius.md};
  border-left: 4px solid ${theme.colors.accent};
  
  .note-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: ${theme.spacing.xs};
  }
  
  .note-meta {
    font-size: ${theme.typography.sizes.xs};
    color: ${theme.colors.gray[500]};
  }
  
  .note-category {
    background: ${theme.colors.accent};
    color: ${theme.colors.white};
    padding: 2px ${theme.spacing.xs};
    border-radius: ${theme.borderRadius.sm};
    font-size: ${theme.typography.sizes.xs};
    font-weight: ${theme.typography.weights.medium};
  }
  
  .note-content {
    color: ${theme.colors.gray[700]};
    font-size: ${theme.typography.sizes.sm};
    line-height: ${theme.typography.lineHeights.relaxed};
  }
`;

const PatientDashboard: React.FC = () => {
  const todayActivities = patientData.daily_activities.find(day => day.date === '2024-01-15');
  const latestVitals = patientData.vital_signs[0];
  const last7Days = patientData.sleep_times.slice(0, 7); // Get last 7 days
  const recentNotes = patientData.care_notes.slice(0, 3);

  // Calculate sleep hours for latest entry
  const calculateSleepHours = (start: string, end: string) => {
    const startTime = new Date(`2024-01-01 ${start}`);
    const endTime = new Date(`2024-01-02 ${end}`); // Next day for end time
    const diff = endTime.getTime() - startTime.getTime();
    return (diff / (1000 * 60 * 60)).toFixed(1);
  };

  return (
    <Wrapper>
      <Container>
        <DashboardContainer>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <TopSection>
            <DashboardCard 
              title="Patient Information" 
              icon={<Icon icon={FiUser} />}
            >
              <PatientInfo>
                <PatientDetails>
                  <h2>{patientData.personal_info.name}</h2>
                  <p><strong>Age:</strong> {patientData.personal_info.age} • <strong>Room:</strong> {patientData.personal_info.room_number}</p>
                  <p><strong>Care Level:</strong> {patientData.personal_info.care_level}</p>
                  <p><strong>Patient ID:</strong> {patientData.patient_id}</p>
                </PatientDetails>
              </PatientInfo>
            </DashboardCard>

            <AllergiesCard>
              <h3>
                <Icon icon={FiAlertCircle} />
                Critical Allergies
              </h3>
              {patientData.special_requirements.allergies.map((allergy, index) => (
                <AllergyItem key={index}>
                  <span style={{ color: '#EF4444', display: 'flex', alignItems: 'center' }}>
                    <Icon icon={FiAlertCircle} />
                  </span>
                  {allergy}
                </AllergyItem>
              ))}
            </AllergiesCard>

            <DashboardCard 
              title="Recent Care Notes" 
              icon={<Icon icon={FiClipboard} />}
            >
              {recentNotes.slice(0, 2).map((note, index) => (
                <CareNote key={index}>
                  <div className="note-header">
                    <span className="note-category">{note.category}</span>
                    <span className="note-meta">{note.date} - {note.author}</span>
                  </div>
                  <p style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {note.note.substring(0, 50)}{note.note.length > 50 && '...'}
                  </p>
                </CareNote>
              ))}
            </DashboardCard>
          </TopSection>

          <StatsGrid>
            <StatCard
              title="Blood Pressure"
              value={latestVitals.blood_pressure}
              subtitle="Latest reading"
              icon={<Icon icon={FiHeart} />}
              color="primary"
            />
            <StatCard
              title="Heart Rate"
              value={`${latestVitals.heart_rate} bpm`}
              subtitle="Within normal range"
              icon={<Icon icon={FiActivity} />}
              color="success"
            />
            <StatCard
              title="Blood Glucose"
              value={`${latestVitals.blood_glucose} mg/dL`}
              subtitle={latestVitals.notes}
              icon={<Icon icon={FiTrendingUp} />}
              color="warning"
            />
            <StatCard
              title="Sleep Quality"
              value={last7Days[0]?.quality || 'N/A'}
              subtitle={`${calculateSleepHours(last7Days[0]?.start_time, last7Days[0]?.end_time)} hours`}
              icon={<Icon icon={FiMoon} />}
              color="secondary"
            />
          </StatsGrid>
          
          <ActivitiesGrid>
            <DashboardCard 
              title="Sleep Calendar (Last 7 Days)" 
              icon={<Icon icon={FiMoon} />}
            >
              <SleepCalendar>
                {last7Days.map((sleep, index) => {
                  const date = new Date(sleep.date);
                  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                  const dayNumber = date.getDate();
                  
                  return (
                    <SleepDay key={index} quality={sleep.quality}>
                      <DayHeader>{dayName}</DayHeader>
                      <DateNumber>{dayNumber}</DateNumber>
                      
                      <SleepTime>
                        <span className="label">Sleep:</span><br />
                        {sleep.start_time}
                      </SleepTime>
                      
                      <SleepTime>
                        <span className="label">Wake:</span><br />
                        {sleep.end_time}
                      </SleepTime>
                      
                      <SleepDuration>
                        {calculateSleepHours(sleep.start_time, sleep.end_time)}h
                      </SleepDuration>
                      
                      <QualityBadge quality={sleep.quality}>
                        {sleep.quality}
                      </QualityBadge>
                    </SleepDay>
                  );
                })}
              </SleepCalendar>
            </DashboardCard>

            <SleepChart sleepData={patientData.sleep_times} />
          </ActivitiesGrid>

          <MainGrid>
            <div>
              <DashboardCard 
                title="Today's Activities" 
                icon={<Icon icon={FiCalendar} />}
                fullHeight
              >
                {todayActivities?.activities.map((activity, index) => (
                  <ActivityItem key={index} status={activity.status}>
                    <ActivityTime>{activity.time}</ActivityTime>
                    <ActivityDescription>
                      <h4>{activity.activity}</h4>
                      {activity.notes && <p>{activity.notes}</p>}
                    </ActivityDescription>
                    <ActivityStatus status={activity.status}>
                      {activity.status === 'completed' ? (
                        <Icon icon={FiCheckCircle} />
                      ) : (
                        <Icon icon={FiClock} />
                      )}
                    </ActivityStatus>
                  </ActivityItem>
                ))}
              </DashboardCard>
            </div>

            <div>
              <DashboardCard 
                title="Medical Information" 
                icon={<Icon icon={FiClipboard} />}
                fullHeight
              >
                <h4 style={{ color: theme.colors.primary, marginBottom: theme.spacing.sm }}>
                  Current Medications
                </h4>
                {patientData.medical_history.medications.slice(0, 4).map((med, index) => (
                  <ListItem key={index}>
                    {med}
                  </ListItem>
                ))}

                <h4 style={{ color: theme.colors.primary, margin: `${theme.spacing.lg} 0 ${theme.spacing.sm} 0` }}>
                  Allergies
                </h4>
                {patientData.special_requirements.allergies.map((allergy, index) => (
                  <ListItem key={index}>
                    <span style={{ color: '#EF4444', marginRight: theme.spacing.xs, display: 'flex', alignItems: 'center' }}>
                      <Icon icon={FiAlertCircle} />
                    </span>
                    {allergy}
                  </ListItem>
                ))}
              </DashboardCard>
          </div>
          </MainGrid>
        </motion.div>
        </DashboardContainer>
    </Container>
    </Wrapper>
  );
};

export default PatientDashboard;